import * as React from 'react';
import {noEmpty} from '@packages/utilities/component';
import {styledDiv} from '@packages/utilities/styled';
import {lighten} from '@packages/systems/core/utils/ColorUtils';
import type {CSSProperties} from '@packages/utilities/css-properties-type';

const PILL_HEIGHT = 16;
const ICON_WIDTH = 8;

const Wrapper = styledDiv(
  ({theme: {colors, control, gutters}, style, dynamic, light}) => ({
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    height: dynamic ? control.heightNarrow : PILL_HEIGHT,
    background: dynamic
      ? colors.logicFlows.backgroundVivid
      : colors.backgroundVivid,
    color: dynamic ? colors.white : colors.control,
    borderWidth: control.borderWidth,
    borderStyle: 'solid',
    borderColor: light
      ? colors.background
      : dynamic
        ? colors.border
        : colors.borderVivid,
    borderRadius: control.borderRadiusPill,
    paddingLeft: gutters.x8,
    paddingRight: gutters.x8,
    ...style,
  }),
  {
    hover: ({style}) => ({
      background: lighten(2)(style.background),
      borderColor: lighten(2)(style.borderColor),
    }),
    highlight: ({theme: {colors}}) => ({
      background: colors.backgroundVivid,
      borderColor: colors.borderVivid,
    }),
  }
);

const Icon = noEmpty(
  styledDiv(({theme}) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.colors.foregroundDim,
    width: ICON_WIDTH,
    height: PILL_HEIGHT,
    marginRight: theme.gutters.x4,
    marginLeft: -theme.gutters.x4 / 2,
  }))
);

const Label = noEmpty(
  styledDiv(({theme, dynamic, style}) => ({
    color: dynamic ? theme.colors.white : theme.colors.foreground,
    fontSize: theme.typography.finePrint.fontSize,
    ...style,
  }))
);

export type Props = {
  icon?: React.ReactNode;
  children: React.ReactNode;
  dynamic?: boolean;
  light?: boolean;
  labelStyle?: Record<any, any>;
  style?: CSSProperties;
  ['data-automation-id']?: string;
};
const PillLabel = ({
  icon,
  children,
  dynamic,
  labelStyle,
  style,
  light,
  ...rest
}: Props) => (
  <Wrapper
    dynamic={dynamic}
    style={style}
    light={light}
    data-legacy-ds="PillLabel"
    {...rest}
  >
    {icon && <Icon>{icon}</Icon>}
    <Label style={labelStyle} dynamic={dynamic}>
      {children}
    </Label>
  </Wrapper>
);

PillLabel.displayName = 'PillLabel';

export default React.memo(PillLabel) as React.ComponentType<Props>;
